import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Modal } from "@/components/base/Modal";
import { canManageListings } from "@/lib/permissions";
import { classifyBcMode, type BcHonestMode } from "@/lib/providers/bigcommerce-adapter";
import { connectorIsReady } from "@/lib/channel-coverage";
import { connectorDisplayName } from "@/lib/connector-identity";
import { parseListingDryRunResult, type ListingDryRunResult } from "@/lib/providers/listing-contract";
import { requestPublication } from "@/lib/channels";

function HonestModeBadge({ mode }: { mode: BcHonestMode }) {
  const styles: Record<BcHonestMode, string> = {
    NOT_CONFIGURED: "bg-red-50 text-red-700 border border-red-200",
    DRY_RUN: "bg-amber-50 text-amber-700 border border-amber-200",
    LIVE: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${styles[mode]}`}>
      {mode === "NOT_CONFIGURED" ? "NOT CONFIGURED" : mode}
    </span>
  );
}

function syncStatusStyle(status: string): string {
  if (status === "synced") return "bg-emerald-50 text-emerald-700";
  if (status === "intended") return "bg-amber-50 text-amber-700";
  if (status === "error") return "bg-red-50 text-red-700";
  return "bg-slate-100 text-slate-500";
}

interface BigcommerceTabProps {
  listingId: string;
  connectorId: string;
  orgId: string;
  categoryId?: string | null;
  categoryName?: string | null;
}

export default function BigcommerceTab({ listingId, connectorId, orgId, categoryId, categoryName }: BigcommerceTabProps) {
  const { role } = useAuth();
  const queryClient = useQueryClient();
  const [activeSubTab, setActiveSubTab] = useState<"overview" | "categories" | "variants" | "media" | "inventory" | "orders">("overview");
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncError, setSyncError] = useState("");
  const [dryRunBusy, setDryRunBusy] = useState(false);
  const [dryRunResult, setDryRunResult] = useState<ListingDryRunResult | null>(null);
  const [planOpen, setPlanOpen] = useState(false);
  const canManage = canManageListings(role);

  const { data: bcConfig } = useQuery({
    queryKey: ["bc-config", orgId, connectorId],
    enabled: !!orgId && !!connectorId,
    queryFn: async () => {
      const { data } = await supabase.from("connector_bigcommerce_config").select("*").eq("connector_id", connectorId).maybeSingle();
      return data;
    },
  });

  const { data: connector } = useQuery({
    queryKey: ["bc-connector-status", orgId, connectorId],
    enabled: !!orgId && !!connectorId,
    queryFn: async () => {
      const { data } = await supabase.from("connectors").select("id, auth_status, name").eq("id", connectorId).maybeSingle();
      return data;
    },
  });

  const { data: bcProducts } = useQuery({
    queryKey: ["bc-products", orgId, listingId],
    enabled: !!orgId && !!listingId,
    queryFn: async () => {
      const { data } = await supabase.from("bigcommerce_products").select("*").eq("listing_id", listingId).order("created_at");
      return (data ?? []) as Array<Record<string, unknown>>;
    },
  });

  const { data: bcVariants } = useQuery({
    queryKey: ["bc-variants", orgId, listingId],
    enabled: !!orgId && !!listingId,
    queryFn: async () => {
      const { data } = await supabase.from("bigcommerce_variants").select("*").eq("listing_id", listingId).order("sku");
      return (data ?? []) as Array<Record<string, unknown>>;
    },
  });

  const { data: bcOptions } = useQuery({
    queryKey: ["bc-options", orgId, listingId],
    enabled: !!orgId && !!listingId,
    queryFn: async () => {
      const { data } = await supabase.from("bigcommerce_options").select("*").eq("listing_id", listingId).order("option_name");
      return (data ?? []) as Array<Record<string, unknown>>;
    },
  });

  const { data: bcMedia } = useQuery({
    queryKey: ["bc-media", orgId, listingId],
    enabled: !!orgId && !!listingId,
    queryFn: async () => {
      const { data } = await supabase.from("bigcommerce_media").select("*").eq("listing_id", listingId).order("sort_order");
      return (data ?? []) as Array<Record<string, unknown>>;
    },
  });

  const { data: orderMappings } = useQuery({
    queryKey: ["bc-order-mappings", orgId, connectorId],
    enabled: !!orgId && !!connectorId,
    queryFn: async () => {
      const { data } = await supabase.from("bigcommerce_order_mappings").select("*").eq("connector_id", connectorId).order("created_at", { ascending: false }).limit(20);
      return (data ?? []) as Array<Record<string, unknown>>;
    },
  });

  const honestMode = classifyBcMode({
    app_credentials_status: (bcConfig as { app_credentials_status?: string } | null)?.app_credentials_status,
    auth_status:
      connectorIsReady((connector as { auth_status?: string } | null)?.auth_status) ||
      connectorIsReady((bcConfig as { auth_status?: string } | null)?.auth_status)
        ? "authorized"
        : ((bcConfig as { auth_status?: string } | null)?.auth_status ?? (connector as { auth_status?: string } | null)?.auth_status),
  });

  const handleDryRun = async () => {
    setDryRunBusy(true);
    setSyncError("");
    try {
      const res = await supabase.functions.invoke("bigcommerce-inventory-sync", {
        body: { listing_id: listingId, org_id: orgId, connector_id: connectorId, mode: "dry_run", sync_type: "full" },
      });

      let rawResult: unknown = res.data;
      if (res.error && !rawResult) {
        const context = (res.error as { context?: { json?: () => Promise<unknown> } }).context;
        if (context?.json) {
          rawResult = await context.json().catch(() => null);
        }
      }
      setDryRunResult(parseListingDryRunResult(rawResult ?? { success: false, error: res.error?.message }));
      setPlanOpen(true);
    } catch (err) {
      setSyncError((err as Error).message);
    }
    setDryRunBusy(false);
  };

  const handleLivePublish = async () => {
    if (honestMode !== "LIVE") {
      setSyncError(`Cannot publish in ${honestMode} mode. Ensure connector is connected and credentials configured.`);
      return;
    }
    setSyncBusy(true);
    setSyncError("");
    try {
      const result = await requestPublication(orgId, listingId);
      if (!result.success) throw new Error(result.error || "Publication request failed");
      queryClient.invalidateQueries({ queryKey: ["bc-products", orgId, listingId] });
      queryClient.invalidateQueries({ queryKey: ["bc-variants", orgId, listingId] });
      queryClient.invalidateQueries({ queryKey: ["bc-options", orgId, listingId] });
      queryClient.invalidateQueries({ queryKey: ["bc-media", orgId, listingId] });
      queryClient.invalidateQueries({ queryKey: ["listing-detail", orgId, listingId] });
    } catch (err) {
      setSyncError((err as Error).message);
    }
    setSyncBusy(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <HonestModeBadge mode={honestMode} />
        {honestMode === "NOT_CONFIGURED" && (
          <span className="text-xs text-red-600">Not connected. Connect your BigCommerce store from the Integrations page.</span>
        )}
        {honestMode === "DRY_RUN" && (
          <span className="text-xs text-amber-600">Connector not authorized. DRY RUN available — no live API calls.</span>
        )}
        {honestMode === "LIVE" && (
          <span className="text-xs text-emerald-600">Connected. Live publishing available.</span>
        )}
      </div>

      <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 w-fit flex-wrap">
        {[
          { key: "overview" as const, label: "Overview", icon: "ri-shopping-bag-3-line" },
          { key: "categories" as const, label: "Categories", icon: "ri-folder-line" },
          { key: "variants" as const, label: `Variants (${bcVariants?.length || 0})`, icon: "ri-grid-line" },
          { key: "media" as const, label: `Media (${bcMedia?.length || 0})`, icon: "ri-image-line" },
          { key: "inventory" as const, label: "Inventory", icon: "ri-archive-drawer-line" },
          { key: "orders" as const, label: `Orders (${orderMappings?.length || 0})`, icon: "ri-file-list-line" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveSubTab(t.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap flex items-center gap-1 transition-colors ${
              activeSubTab === t.key ? "bg-emerald-50 text-emerald-700" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            <i className={t.icon} />
            {t.label}
          </button>
        ))}
      </div>

      {syncError && (
        <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{syncError}</div>
      )}

      {activeSubTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">BigCommerce Listing Info</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Mode</span><HonestModeBadge mode={honestMode} /></div>
              <div className="flex justify-between"><span className="text-slate-500">Store</span><span className="text-slate-800">{connectorDisplayName({
                id: connectorId,
                name: (connector as { name?: string } | null)?.name ?? "",
                provider: "bigcommerce",
                authStatus: (connector as { auth_status?: string } | null)?.auth_status,
                storefrontOrigin: (bcConfig as { storefront_origin?: string | null } | null)?.storefront_origin,
                storeHash: (bcConfig as { store_hash?: string | null } | null)?.store_hash,
              })}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Currency</span><span className="text-slate-800">{bcConfig?.currency || "USD"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Category</span><span className="text-slate-800">{categoryName || categoryId || "Not set"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Products</span><span className="text-slate-800">{bcProducts?.length || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Variants</span><span className="text-slate-800">{bcVariants?.length || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Options</span><span className="text-slate-800">{bcOptions?.length || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Images</span><span className="text-slate-800">{bcMedia?.length || 0}</span></div>
            </div>

            {canManage && (
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                <button
                  onClick={handleDryRun}
                  disabled={dryRunBusy || honestMode === "NOT_CONFIGURED"}
                  className="px-3 py-1.5 bg-amber-50 border border-amber-200 hover:bg-amber-100 disabled:opacity-60 text-amber-700 text-xs font-medium rounded-lg whitespace-nowrap"
                  title={honestMode === "NOT_CONFIGURED" ? "Configure app credentials first" : "Generate plan and validate without calling BigCommerce"}
                >
                  {dryRunBusy ? "Validating..." : "Validate / Dry Run"}
                </button>
                <button
                  onClick={handleLivePublish}
                  disabled={syncBusy || honestMode !== "LIVE"}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap ${
                    honestMode === "LIVE"
                      ? "bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white"
                      : "bg-slate-100 text-slate-400 cursor-not-allowed"
                  }`}
                  title={honestMode !== "LIVE" ? `Requires LIVE mode (currently ${honestMode})` : "Publish to BigCommerce"}
                >
                  {syncBusy ? "Publishing..." : "Publish to BigCommerce"}
                </button>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Provider-Confirmed State</h3>
            {bcProducts && bcProducts.length > 0 ? (
              <div className="space-y-2 text-sm">
                {bcProducts.map((p) => (
                  <div key={p.id as string}>
                    <div className="flex justify-between">
                      <span className="text-slate-500">External Product ID</span>
                      <span className="font-mono text-xs text-slate-700">{String(p.external_product_id ?? "—")}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Provider Confirmed</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${p.provider_confirmed_at ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                        {p.provider_confirmed_at ? new Date(p.provider_confirmed_at as string).toLocaleDateString() : "Not confirmed"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Sync Status</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${syncStatusStyle(String(p.sync_status ?? ""))}`}>{String(p.sync_status ?? "")}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400">No BigCommerce product tracked yet. Publish to create tracked state.</p>
            )}
          </div>
        </div>
      )}

      {activeSubTab === "categories" && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Category Mapping</h3>
          <p className="text-sm text-slate-500 mb-4">
            BigCommerce category mapped to this listing. Categories are scoped per connector — Store A categories cannot be used by Store B.
          </p>
          {categoryId ? (
            <div className="p-3 border border-slate-100 rounded-lg">
              <p className="text-xs text-slate-500">Mapped Category</p>
              <p className="text-sm font-medium text-slate-800">{categoryName || categoryId}</p>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
              No category mapped. Select a BigCommerce category for this listing.
            </div>
          )}
        </div>
      )}

      {activeSubTab === "variants" && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">BigCommerce Variants</h3>
            <p className="text-xs text-slate-400 mt-1">
              Variants with <strong>intended</strong> status are validated but not confirmed. <strong>Synced</strong> means provider-confirmed.
            </p>
          </div>
          {!bcVariants || bcVariants.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No variants tracked yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">SKU</th>
                    <th className="px-4 py-3 font-medium">External ID</th>
                    <th className="px-4 py-3 font-medium">Sync Status</th>
                    <th className="px-4 py-3 font-medium">Provider Confirmed</th>
                    <th className="px-4 py-3 font-medium">Price</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {bcVariants.map((v) => (
                    <tr key={v.id as string}>
                      <td className="px-4 py-3 font-mono text-xs text-slate-700">{v.sku as string || "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-400">{String(v.external_variant_id ?? "—")}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${syncStatusStyle(String(v.sync_status ?? ""))}`}>{String(v.sync_status ?? "")}</span>
                      </td>
                      <td className="px-4 py-3">
                        {v.provider_confirmed_at ? (
                          <span className="text-xs text-emerald-600">{new Date(v.provider_confirmed_at as string).toLocaleString()}</span>
                        ) : (
                          <span className="text-xs text-slate-400">Not confirmed</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">{v.intended_price ? `$${v.intended_price}` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeSubTab === "media" && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">BigCommerce Media</h3>
          </div>
          {!bcMedia || bcMedia.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No media tracked yet.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 p-4">
              {bcMedia.map((m, i) => (
                <div key={i} className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="aspect-square bg-slate-100 flex items-center justify-center">
                    <i className="ri-image-line text-2xl text-slate-300" />
                  </div>
                  <div className="p-2">
                    <p className="text-xs font-medium text-slate-700 truncate">{m.image_url ? (m.image_url as string).split("/").pop() : "Image"}</p>
                    {m.is_thumbnail && <span className="text-xs bg-emerald-50 text-emerald-700 px-1 py-0.5 rounded">Thumbnail</span>}
                    <p className="text-xs text-slate-400 mt-1">
                      {m.sync_status === "synced" ? "Synced" : m.sync_status === "intended" ? "Intended" : m.sync_status as string}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeSubTab === "inventory" && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Inventory Sync</h3>
          <p className="text-sm text-slate-500">
            Inventory sync uses canonical available quantity and channel allocations. BigCommerce inventory remains a reflection — Tradivo is the single source of truth.
          </p>
          <div className="mt-4 space-y-2">
            {bcVariants?.map((v) => (
              <div key={v.id as string} className="flex justify-between items-center p-3 border border-slate-100 rounded-lg">
                <span className="text-sm text-slate-700 font-mono text-xs">{String(v.sku ?? "—")}</span>
                <span className="text-sm text-slate-600">
                  Intended: <span className="font-medium">{v.intended_inventory != null ? String(v.intended_inventory) : "—"}</span>
                </span>
              </div>
            )) || <p className="text-sm text-slate-400">No variant inventory tracked.</p>}
          </div>
        </div>
      )}

      {activeSubTab === "orders" && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">BigCommerce Order Mappings</h3>
          </div>
          {!orderMappings || orderMappings.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No orders imported yet.</div>
          ) : (
            <div className="divide-y divide-slate-50">
              {orderMappings.map((order) => (
                <div key={order.id as string} className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-800">Order #{order.external_order_id as number}</p>
                    <p className="text-xs text-slate-400">{order.order_status as string || "unknown"} · {order.payment_status as string || "unknown"}</p>
                    {order.sales_order_id && <p className="text-xs text-emerald-600">Linked to Sales Order</p>}
                  </div>
                  <span className="text-xs text-slate-400">{new Date(order.created_at as string).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Modal
        open={planOpen}
        onClose={() => setPlanOpen(false)}
        title="Validate / Dry Run Result"
        footer={
          <button onClick={() => setPlanOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
            Close
          </button>
        }
      >
        {dryRunResult && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <HonestModeBadge mode={dryRunResult.mode} />
              <span className="text-sm text-slate-600">{dryRunResult.message || dryRunResult.error || ""}</span>
            </div>
            {dryRunResult.intended_product && (
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1">Intended Product:</p>
                <p className="text-xs text-slate-600">{dryRunResult.intended_product.name}</p>
              </div>
            )}
            <p className="text-xs text-amber-600">
              Note: These are DRY RUN intentions only. No external IDs were created. No provider-confirmed state was set.
            </p>
            {(dryRunResult.blockers?.length ?? 0) > 0 && (
              <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                <p className="text-xs font-medium text-red-700 mb-1">Blocking Issues:</p>
                {dryRunResult.blockers!.map((b, i) => (
                  <p key={i} className="text-xs text-red-600">{b.code}: {b.message}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
